import { useTranslation } from 'react-i18next';
import { ApiRequestError, ApiTimeoutError, UNKNOWN_REQUEST_ID } from '@/lib/api-client';

/**
 * Translates a thrown API error into a message for the user.
 *
 * The server's stable `code` is what gets translated; its English `message` is
 * never shown, so the UI stays fully Arabic without the backend owning any
 * copy.
 */
export function useApiError() {
  const { t } = useTranslation('errors');

  return (error: unknown): { message: string; reference?: string } => {
    // Rule E8: the request may already have succeeded server-side - this is
    // never worded like a confirmed failure, and (rule E17) never invites a
    // blind retry of a consequential mutation. Rule E15: nothing was
    // confirmed logged for this attempt, so there is no reference to show.
    if (error instanceof ApiTimeoutError) return { message: t('timeout') };

    if (error instanceof ApiRequestError) {
      return {
        message: t(error.code, { ...error.params, defaultValue: t('INTERNAL_ERROR') }),
        // Rule E15: only a reference the server actually issued. The synthetic
        // fallback identifies nothing, and showing it sends a user to support
        // with an id no one can look up.
        ...(error.requestId === UNKNOWN_REQUEST_ID
          ? {}
          : { reference: t('referenceId', { requestId: error.requestId }) }),
      };
    }

    return { message: t('network') };
  };
}
